import { Box, Stack } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import { type FC } from 'react';
import { liquidGlassCircleShadowSx, liquidGlassSurfaceFillSx } from '../helpers/liquidGlassSurface';
import { MAX_VOICE_RECORDING_SECONDS, useVoiceCapture } from '../hooks/useVoiceCapture';
import { CommonIcon } from './CommonIcon';
import { PremiumStarBadge } from './PremiumStarBadge';

const VOICE_KEYFRAMES = `
  @keyframes voiceWaveBar { from { height: 4px } to { height: 36px } }
`;

const SPEAK_BUTTON_SIZE = 64;

const formatMMSS = (seconds: number) =>
    `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;

type WaveBarProps = {
    delay: number;
    color?: string;
};

const WaveBar: FC<WaveBarProps> = ({ delay, color }) => {
    const base = color ?? 'var(--tg-theme-button-color)';
    const gradient = `linear-gradient(180deg, ${base}, color-mix(in srgb, ${base} 70%, white))`;
    return (
        <Box
            sx={{
                width: 3,
                borderRadius: 4,
                background: gradient,
                animation: 'voiceWaveBar 0.55s ease-in-out infinite alternate',
                animationDelay: `${delay}ms`,
                minHeight: 4,
                maxHeight: 36,
            }}
        />
    );
};

export type VoiceCaptureButtonProps = {
    onTranscript: (text: string) => void;
    getLanguage?: () => string;
    labels: {
        processing: string;
        stopRecording: string;
        tapToSpeak: string;
    };
    buttonColor?: string;
    buttonTextColor?: string;
    locked?: boolean;
    onLockedPress?: () => void;
};

export const VoiceCaptureButton: FC<VoiceCaptureButtonProps> = ({
    onTranscript,
    getLanguage,
    labels,
    buttonColor,
    buttonTextColor,
    locked = false,
    onLockedPress,
}) => {
    const { isRecording, recordingElapsedSeconds, isProcessing, error, toggle } = useVoiceCapture(onTranscript, getLanguage);
    const disabled = isProcessing;
    const buttonBg = buttonColor ?? 'var(--tg-theme-button-color)';
    const textColor = buttonTextColor ?? 'var(--tg-theme-button-text-color)';
    const waveColor = buttonColor ? `color-mix(in srgb, ${buttonColor} 75%, white)` : undefined;
    const ariaLabel = isProcessing ? labels.processing : isRecording ? labels.stopRecording : labels.tapToSpeak;

    return (
        <>
            <style>{VOICE_KEYFRAMES}</style>
            <Stack alignItems="center" gap={1} sx={{ width: '100%' }}>
                {isRecording && (
                    <Stack direction="row" alignItems="center" justifyContent="center" gap={1} sx={{ minHeight: 36 }}>
                        <Stack direction="row" alignItems="center" gap={0.375}>
                            {Array.from({ length: 16 }).map((_, index) => (
                                <WaveBar key={index} delay={index * 40} color={waveColor} />
                            ))}
                        </Stack>
                        <Text
                            style={{
                                fontSize: 12,
                                fontVariantNumeric: 'tabular-nums',
                                color: 'var(--tg-theme-hint-color)',
                            }}
                        >
                            {`${formatMMSS(recordingElapsedSeconds)} / ${formatMMSS(MAX_VOICE_RECORDING_SECONDS)}`}
                        </Text>
                    </Stack>
                )}
                <Box
                    sx={{
                        position: 'relative',
                        width: SPEAK_BUTTON_SIZE,
                        height: SPEAK_BUTTON_SIZE,
                        borderRadius: '50%',
                        overflow: 'visible',
                        opacity: disabled ? 0.7 : 1,
                        ...liquidGlassCircleShadowSx,
                    }}
                >
                    <Box
                        component="button"
                        type="button"
                        onClick={locked ? onLockedPress : toggle}
                        disabled={disabled}
                        aria-label={ariaLabel}
                        aria-busy={isProcessing || undefined}
                        sx={{
                            width: '100%',
                            height: '100%',
                            p: 0,
                            m: 0,
                            borderRadius: 'inherit',
                            border: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: disabled ? 'var(--tg-theme-hint-color)' : isRecording ? textColor : 'var(--tg-theme-text-color)',
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            WebkitTapHighlightColor: 'transparent',
                            outline: 'none',
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            transition: 'background-color 0.2s ease, color 0.2s ease',
                            bgcolor: isRecording ? buttonBg : undefined,
                            ...(isRecording ? {} : liquidGlassSurfaceFillSx),
                            '&:focus-visible': {
                                outline: '2px solid var(--tg-theme-link-color)',
                                outlineOffset: 2,
                            },
                        }}
                    >
                        <CommonIcon icon={isRecording ? 'stop' : 'mic'} type="filled" size={28} color="inherit" />
                    </Box>
                    {locked ? (
                        <Box
                            component="span"
                            role="img"
                            aria-label="Premium"
                            sx={{ position: 'absolute', top: 0, right: 0, width: 14, height: 14 }}
                        >
                            <PremiumStarBadge color="var(--tgui--button_color)" sx={{ position: 'static' }} />
                        </Box>
                    ) : null}
                </Box>
            </Stack>
            {error ? (
                <Text
                    style={{
                        fontSize: 12,
                        color: 'var(--tg-theme-destructive-text-color)',
                        marginTop: 4,
                        textAlign: 'center',
                        display: 'block',
                    }}
                >
                    {error}
                </Text>
            ) : null}
        </>
    );
};
